import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import posthog from "posthog-js";
import { PostHogProvider, PostHogErrorBoundary } from "@posthog/react";
import CrashFallback from "./components/crash-fallback";
import { installDomTranslateGuard } from "./domTranslateGuard";

// Disable PostHog in local dev (`npm start`) and on localhost — keeps our
// production analytics clean of debug noise and avoids capturing/identifying
// fake test users into the real project. `NODE_ENV` is the build-time signal
// (CRA sets it to `development` for `npm start`); the hostname check covers
// the rare case of serving the production build locally for QA.
const isLocalEnv =
  process.env.NODE_ENV !== "production" ||
  process.env.REACT_APP_DEPLOY_ENV === "dev" ||
  (typeof window !== "undefined" &&
    ["localhost", "127.0.0.1"].includes(window.location.hostname));

// Initialised HERE, before React renders, not by <PostHogProvider apiKey>.
// The provider inits in its own useEffect, and React runs a child's effects
// before its parent's — so any capture a page made on its first load ran
// against an uninitialised client and was dropped. That is why
// `checkout_completed` (captured on /upgrade/success's first render, straight
// after Stripe's redirect) had never once been recorded.
posthog.init("phc_D1i7ZLu5FlbkODJ9ZygC5MDf146WgRfLuqw3NcUpEk4", {
  api_host: "https://app.posthog.com",
  // Local dev must still LOAD feature flags — the Featurebase kill switch
  // `featurebase-live-chat` is evaluated client-side, so localhost needs
  // the /flags response to honor it — but must send ZERO events into the
  // production project. Opting out would suppress the flags request too,
  // so instead we keep flags enabled and drop every captured event via
  // `before_send`. Net locally: feature flags in, zero events out.
  autocapture: !isLocalEnv,
  capture_exceptions: !isLocalEnv,
  capture_pageview: !isLocalEnv,
  capture_pageleave: !isLocalEnv,
  disable_session_recording: isLocalEnv,
  before_send: isLocalEnv ? () => null : undefined,
  loaded: (client) => {
    if (isLocalEnv) {
      // Earlier builds called `opt_out_capturing()`, which PERSISTS an
      // opt-out in storage and suppresses the /flags request too. Opt
      // back in (clearing that persisted state) and force a flags fetch
      // so the value is available this session; `before_send` above still
      // drops every event, so nothing reaches the production project.
      client.opt_in_capturing();
      client.reloadFeatureFlags();
      // eslint-disable-next-line no-console
      console.info(
        "[posthog] local env — events dropped, feature flags still loaded (e.g. featurebase-live-chat)"
      );
    }
  },
});

installDomTranslateGuard();

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);
root.render(
  <React.StrictMode>
    <PostHogProvider client={posthog}>
      <PostHogErrorBoundary fallback={<CrashFallback />}>
        <App />
      </PostHogErrorBoundary>
    </PostHogProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
